import React, { useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import DataTable from 'react-data-table-component';
import { getPaymentListing, updatePaymentStatus } from '../../api/context/orders';
import Loader from '../../components/loader/loader';
import { Box, Paper, Tooltip } from "@mui/material";
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { checkUserPermissions } from '../../helpers/permission';
import { IoSearchOutline } from 'react-icons/io5';
import toast from 'react-hot-toast';
import { IoIosCheckmarkCircle } from 'react-icons/io';


const queueDatatableCustomStyles = {
  headRow: {
    style: {
      backgroundColor: '#f8fafc',
      borderBottom: '1px solid #e2e8f0',
      minHeight: '44px',
    },
  },
  headCells: {
    style: {
      display: 'flex',
      justifyContent: 'center',
      fontWeight: '600',
      fontSize: '9px',
      padding: '10px 6px',
      color: '#475569',
      textTransform: 'uppercase',
      letterSpacing: '0.02em',
      backgroundColor: '#f8fafc',
    },
  },
  cells: {
    style: {
      display: 'flex',
      justifyContent: 'center',
      fontSize: '11px',
      fontWeight: '500',
      color: '#0f172a',
      padding: '10px 6px',
    },
  },
  rows: {
    style: {
      minHeight: '48px',
      borderBottom: '1px solid #eef2f7',
      transition: 'background 0.15s ease',
      '&:hover': {
        backgroundColor: 'rgba(0,134,149,0.04) !important',
        cursor: 'pointer',
      },
    },
  },
};

const paymentStatusChip = (status) => {
  if (status === 'DONE') {
    return { label: 'Completed', bg: 'rgba(34,197,94,0.10)', color: '#15803d', border: 'rgba(34,197,94,0.25)' };
  }
  return { label: 'Pending', bg: 'rgba(245,158,11,0.12)', color: '#b45309', border: 'rgba(245,158,11,0.28)' };
};

const refundStatusChip = (status) => {
  if (status === 'processed') return { label: 'Processed', bg: 'rgba(34,197,94,0.10)', color: '#15803d', border: 'rgba(34,197,94,0.25)' };
  if (status === 'pending')   return { label: 'Pending',   bg: 'rgba(245,158,11,0.12)', color: '#b45309', border: 'rgba(245,158,11,0.28)' };
  if (status === 'failed')    return { label: 'Failed',    bg: 'rgba(239,68,68,0.10)',  color: '#b91c1c', border: 'rgba(239,68,68,0.28)' };
  return { label: '—', bg: 'rgba(148,163,184,0.10)', color: '#64748b', border: 'rgba(148,163,184,0.25)' };
};

const Payment = () => {
  const navigate = useNavigate()

  const [payment, setPayment] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [update, setUpdate] = useState(false);
  const [initial, setInitial] = useState(false);
  const [search, setSearch] = useState("");

  const userType = JSON.parse(localStorage.getItem('user'))?.userType;

  const handleUpdateStatus = async (orderId) => {
    try {
      setLoading(true);
      updatePaymentStatus({ orderId })((response) => {
        if (response.statusCode === 200) {
          setUpdate((prev) => !prev);
          toast.success("Payment status updated successfully");
        } else {
          toast.error("Failed to update payment status");
        }
        setLoading(false);
      });
    } catch (error) {
      console.error("Error updating payment status:", error);
      toast.error("An error occurred while updating payment status");
      setLoading(false);
    }
  };

  const columns = [
    {
      id: 'orderId',
      name: 'Order Id',
      selector: (row) => row?.orderId,
      cell: (row) => (
        <Tooltip title={row?.orderId} disableInteractive placement='top'>
          <div
            className="pay-order-chip"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/orderdetails/${row.orderId}`);
            }}
          >
            ODR-00000{row?.orderId}
          </div>
        </Tooltip>
      ),
      grow: 0,
      width: '150px',
      center: true,
    },
    {
      id: 'transactionId',
      name: 'Transaction Id',
      selector: (row) => row?.transactionId,
      cell: (row) => (
        <Tooltip title={row?.transactionId || '—'} disableInteractive placement='top'>
          <div className='text-truncate' style={{ maxWidth: 180 }}>{row?.transactionId || '—'}</div>
        </Tooltip>
      ),
      grow: 1,
      minWidth: '160px',
      center: true,
    },
    {
      id: 'amount',
      name: 'Amount',
      selector: (row) => row?.amount,
      cell: (row) => <span className="pay-amount">₹ {Number(row?.amount || 0).toLocaleString('en-IN')}</span>,
      grow: 0,
      width: '120px',
      center: true,
    },
    {
      id: 'status',
      name: 'Status',
      selector: (row) => row?.Order?.paymentStatus,
      cell: (row) => {
        const chip = paymentStatusChip(row?.Order?.paymentStatus);
        return (
          <span
            className="pay-chip"
            style={{ background: chip.bg, color: chip.color, borderColor: chip.border }}
          >
            {chip.label}
          </span>
        );
      },
      sortable: true,
      grow: 0,
      width: '120px',
      center: true,
    },
    {
      id: 'paymentMode',
      name: 'Mode',
      selector: (row) => row?.Order?.paymentMode,
      cell: (row) => (
        <Tooltip title={row?.Order?.paymentMode || '—'} disableInteractive placement='top'>
          <div className='text-truncate' style={{ maxWidth: 120 }}>{row?.Order?.paymentMode || '—'}</div>
        </Tooltip>
      ),
      grow: 0,
      width: '110px',
      center: true,
    },
    {
      id: 'createdAt',
      name: 'Date',
      selector: (row) => moment(row?.createdAt).format('DD/MM/YYYY'),
      cell: (row) => (
        <Tooltip title={moment(row?.createdAt).format('DD/MM/YYYY, hh:mm a')} disableInteractive placement='top'>
          <div>{moment(row?.createdAt).format('DD MMM YY')}</div>
        </Tooltip>
      ),
      grow: 0,
      width: '110px',
      center: true,
    },
    {
      id: 'refundId',
      name: 'Refund Id',
      selector: (row) => row?.refundId,
      cell: (row) => (
        <Tooltip title={row?.refundId || '—'} disableInteractive placement='top'>
          <div className='text-truncate' style={{ maxWidth: 160 }}>{row?.refundId || '—'}</div>
        </Tooltip>
      ),
      grow: 1,
      minWidth: '140px',
      center: true,
    },
    {
      id: 'refundStatus',
      name: 'Refund Status',
      selector: (row) => row?.refundStatus,
      cell: (row) => {
        const chip = refundStatusChip(row?.refundStatus);
        return (
          <span
            className="pay-chip"
            style={{ background: chip.bg, color: chip.color, borderColor: chip.border }}
          >
            {chip.label}
          </span>
        );
      },
      grow: 0,
      width: '120px',
      center: true,
    },
    {
      id: 'refundInitiatedAt',
      name: 'Refund Date',
      selector: (row) => row?.refundInitiatedAt ? moment(row?.refundInitiatedAt).format('DD/MM/YYYY') : '—',
      cell: (row) => (
        <Tooltip title={row?.refundInitiatedAt ? moment(row?.refundInitiatedAt).format('DD/MM/YYYY, hh:mm a') : '—'} disableInteractive placement='top'>
          <div>{row?.refundInitiatedAt ? moment(row?.refundInitiatedAt).format('DD MMM YY') : '—'}</div>
        </Tooltip>
      ),
      grow: 0,
      width: '120px',
      center: true,
    },
    ...(userType === "ADMIN"
      ? [
        {
          name: 'Action',
          cell: (row) => (
            checkUserPermissions("manage_payments") && row?.Order?.paymentStatus === "PENDING" ? (
              <Tooltip title="Mark as Completed" disableInteractive placement="top">
                <button
                  type="button"
                  className="pay-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpdateStatus(row?.Order?.id);
                  }}
                >
                  <IoIosCheckmarkCircle size={16} />
                  <span>Mark done</span>
                </button>
              </Tooltip>
            ) : <span style={{ color: '#94a3b8', fontSize: 10 }}>—</span>
          ),
          grow: 0,
          width: '130px',
          ignoreRowClick: true,
          allowOverflow: true,
          center: true,
        },
      ]
      : []),
  ];

  const fetchPaymentData = async () => {
    const params = { page, pageSize: limit };
    if (search && search.trim() !== "") params.search = search;
    getPaymentListing(params)((response) => {
      if (response?.statusCode === 200) {
        setPayment(response?.data?.orders);
        setTotalRows(response?.data?.totalItems);
      } else {
        setPayment([]);
      }
      setLoading(false);
    });
  };

  const handlePerRowsChange = (newPerPage) => {
    setLimit(newPerPage);
    setPage(1);
    setUpdate(!update);
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    setUpdate(!update);
  };

  useEffect(() => {
    setLoading(true);
    fetchPaymentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (initial) setUpdate(!update);
      else setInitial(true);
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div className="payments-page">
      <style>{`
        /* Match homepage: soft neutral background + consistent 16px horizontal gutter */
        .payments-page { background: #f9fafb; min-height: calc(100vh - 70px); }
        .main-content:has(.payments-page) { background: #f9fafb; }
        .page-content:has(.payments-page) { background: #f9fafb; }

        body:has(.payments-page) .page-content,
        body:has(.payments-page) #layout-wrapper.no-topbar .page-content,
        body:has(.payments-page) #layout-wrapper .page-content {
          padding-top: 0 !important;
          padding-bottom: 24px !important;
          padding-left: 16px !important;
          padding-right: 16px !important;
        }
        body:has(.payments-page) .container-fluid,
        body:has(.payments-page) .container {
          padding-left: 0 !important;
          padding-right: 0 !important;
        }

        /* Sticky header — bleeds edge-to-edge; horizontal padding matches .modern-panel-header */
        .payments-page .page-header-sticky {
          padding: clamp(14px, 1.6vw, 24px) clamp(14px, 1.4vw, 20px) clamp(10px, 1.2vw, 18px) !important;
          margin: 0 -16px 16px !important;
          top: 0 !important;
          background: #f9fafb !important;
          border-bottom: 1px solid #eef2f7 !important;
          box-shadow: none !important;
        }
        @media (max-width: 991px) {
          .payments-page .page-header-sticky {
            padding: 16px 16px 12px !important;
          }
        }
        @media (max-width: 767.98px) {
          .payments-page .page-header-sticky {
            padding: 10px 16px 8px !important;
            margin: 0 -16px 8px !important;
            position: static !important;
          }
        }
        @media (max-width: 480px) {
          .payments-page .page-header-sticky {
            padding: 10px 16px 8px !important;
            margin: 0 -16px 8px !important;
          }
        }

        .payments-page .pay-search-row {
          display: flex; align-items: center; gap: 8px;
          margin: 12px 0;
        }
        .payments-page .pay-search {
          position: relative; flex: 1 1 auto; min-width: 0;
        }
        .payments-page .pay-search input {
          width: 100%;
          height: 34px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 6px 12px 6px 34px;
          font-size: 12px;
          color: #0f172a;
          background: #fff;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .payments-page .pay-search input:focus {
          border-color: #008695;
          box-shadow: 0 0 0 3px rgba(0,134,149,0.12);
          outline: none;
        }
        .payments-page .pay-search svg {
          position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
          color: #94a3b8;
        }
        @media (max-width: 767.98px) {
          .payments-page .pay-search-row { margin: 0 0 8px !important; }
        }

        /* DataTable container */
        .payments-page .products-and-options .MuiPaper-root {
          border: 1px solid #e2e8f0 !important;
          border-radius: 10px !important;
          overflow: hidden;
          box-shadow: none !important;
        }

        /* Cells / chips */
        .payments-page .pay-order-chip {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 10px;
          font-weight: 600;
          color: #0f172a;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          cursor: pointer;
          transition: background 0.12s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .payments-page .pay-order-chip:hover {
          background: rgba(0,134,149,0.08);
          border-color: rgba(0,134,149,0.35);
          color: #008695;
        }
        .payments-page .pay-amount {
          color: #008695;
          font-weight: 700;
          font-size: 12px;
          letter-spacing: -0.01em;
        }
        .payments-page .pay-chip {
          display: inline-flex;
          align-items: center;
          padding: 3px 10px;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.01em;
          border: 1px solid transparent;
          white-space: nowrap;
        }
        .payments-page .pay-action-btn {
          display: inline-flex; align-items: center; gap: 4px;
          height: 28px;
          padding: 0 10px;
          font-size: 10.5px;
          font-weight: 600;
          border-radius: 6px;
          border: 1px solid rgba(34,197,94,0.35);
          background: rgba(34,197,94,0.10);
          color: #15803d;
          cursor: pointer;
          transition: background 0.12s ease, border-color 0.12s ease;
        }
        .payments-page .pay-action-btn:hover {
          background: rgba(34,197,94,0.16);
          border-color: rgba(34,197,94,0.45);
        }

        /* Mobile card list */
        .payments-page .pay-mobile-list { display: none; }
        .payments-page .pay-mobile-pagination { display: none; }
        @media (max-width: 767.98px) {
          .payments-page .products-and-options { display: none !important; }
          .payments-page .pay-mobile-list {
            display: flex;
            flex-direction: column;
            gap: 10px;
            padding: 0 0 calc(60px + env(safe-area-inset-bottom, 0px));
          }

          /* Fixed bottom pagination bar */
          .payments-page .pay-mobile-pagination {
            display: flex !important;
            position: fixed;
            left: 0; right: 0; bottom: 0;
            padding: 8px 12px calc(8px + env(safe-area-inset-bottom, 0px));
            background: #fff;
            border-top: 1px solid #e2e8f0;
            box-shadow: 0 -2px 8px rgba(15,23,42,0.05);
            z-index: 1049;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
          }
          .payments-page .pmp-btn {
            width: 40px; height: 36px;
            border-radius: 6px;
            border: 1px solid #e2e8f0;
            background: #fff;
            color: #0f172a;
            font-size: 16px;
            font-weight: 700;
            cursor: pointer;
            display: inline-flex; align-items: center; justify-content: center;
            transition: all 0.15s ease;
          }
          .payments-page .pmp-btn:hover:not(:disabled) {
            border-color: #008695;
            color: #008695;
            background: rgba(0,134,149,0.06);
          }
          .payments-page .pmp-btn:disabled {
            opacity: 0.35;
            cursor: not-allowed;
          }
          .payments-page .pmp-info {
            font-size: 11px;
            color: #475569;
            display: inline-flex;
            align-items: baseline;
            gap: 4px;
            flex: 1;
            justify-content: center;
            text-align: center;
          }
          .payments-page .pmp-info strong { color: #008695; font-weight: 700; }
          .payments-page .pmp-total { color: #94a3b8; font-size: 10px; }
          .payments-page .pay-card {
            background: #fff;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 12px;
            cursor: pointer;
            transition: transform 0.12s ease, border-color 0.15s ease;
          }
          .payments-page .pay-card:active { transform: scale(0.99); border-color: #008695; }
          .payments-page .pay-card-top {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;
          }
          .payments-page .pay-card-amount {
            color: #008695;
            font-weight: 800;
            font-size: 16px;
            letter-spacing: -0.02em;
            margin: 4px 0 8px;
          }
          .payments-page .pay-card-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 6px;
            font-size: 10.5px;
          }
          .payments-page .pay-card-row + .pay-card-row { margin-top: 4px; }
          .payments-page .pay-card-label { color: #64748b; }
          .payments-page .pay-card-value { color: #0f172a; font-weight: 600; text-align: right; word-break: break-all; max-width: 60%; }
          .payments-page .pay-card-action-row { margin-top: 10px; display: flex; justify-content: flex-end; }
        }

        /* Empty state — used in both the DataTable and mobile card list */
        .payments-page .pay-empty {
          width: 100%;
          min-height: 340px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 32px 20px;
          text-align: center;
          color: #64748b;
          background: linear-gradient(180deg, #fafbfd 0%, #f5f7fa 100%);
          border: 1px solid #eef2f7;
          border-radius: 12px;
        }
        .payments-page .pay-empty-icon {
          font-size: 40px;
          margin-bottom: 12px;
          opacity: 0.7;
          filter: grayscale(0.2);
        }
        .payments-page .pay-empty-title {
          font-size: 15px;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.01em;
          margin: 0 0 4px;
        }
        .payments-page .pay-empty-sub {
          font-size: 12px;
          color: #94a3b8;
          font-weight: 500;
          margin: 0;
        }
        @media (max-width: 767.98px) {
          .payments-page .pay-empty { min-height: 380px; }
        }
      `}</style>

      <div className="page-header-sticky">
        <h3 className="fw-bold" style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.01em' }}>Payments</h3>
      </div>

      <div className="pay-search-row">
        <div className="pay-search">
          <IoSearchOutline size={14} />
          <input
            type="text"
            placeholder="Search payments by Order Id..."
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Desktop DataTable */}
      <Box className="products-and-options">
        <Paper square className='shadow-none border-0'>
          <DataTable
            columns={columns}
            data={payment}
            progressPending={loading}
            progressComponent={<div style={{ height: '60vh' }}><Loader /></div>}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationRowsPerPageOptions={[10, 50, 100, 150]}
            paginationPerPage={limit}
            onRowClicked={(row) => navigate('/paymentdetails', { state: { paymentDetails: row } })}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            currentPage={page}
            customStyles={queueDatatableCustomStyles}
            noDataComponent={
              <div className="pay-empty">
                <div className="pay-empty-icon" aria-hidden="true">💳</div>
                <p className="pay-empty-title">No payments found</p>
                <p className="pay-empty-sub">Payments will appear here once orders are processed.</p>
              </div>
            }
          />
        </Paper>
      </Box>

      {/* Mobile card list */}
      <div className="pay-mobile-list">
        {loading ? (
          <div style={{ height: '40vh' }}><Loader /></div>
        ) : payment && payment.length > 0 ? (
          payment.map((row) => {
            const chip = paymentStatusChip(row?.Order?.paymentStatus);
            const rChip = refundStatusChip(row?.refundStatus);
            return (
              <div
                key={row.id || row.transactionId}
                className="pay-card"
                onClick={() => navigate('/paymentdetails', { state: { paymentDetails: row } })}
              >
                <div className="pay-card-top">
                  <div
                    className="pay-order-chip"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/orderdetails/${row.orderId}`);
                    }}
                  >
                    ODR-00000{row?.orderId}
                  </div>
                  <span
                    className="pay-chip"
                    style={{ background: chip.bg, color: chip.color, borderColor: chip.border }}
                  >
                    {chip.label}
                  </span>
                </div>

                <div className="pay-card-amount">₹ {Number(row?.amount || 0).toLocaleString('en-IN')}</div>

                <div className="pay-card-row">
                  <span className="pay-card-label">Txn Id</span>
                  <span className="pay-card-value">{row?.transactionId || '—'}</span>
                </div>
                <div className="pay-card-row">
                  <span className="pay-card-label">Mode</span>
                  <span className="pay-card-value">{row?.Order?.paymentMode || '—'}</span>
                </div>
                <div className="pay-card-row">
                  <span className="pay-card-label">Date</span>
                  <span className="pay-card-value">{moment(row?.createdAt).format('DD MMM YY, hh:mm a')}</span>
                </div>
                <div className="pay-card-row">
                  <span className="pay-card-label">Refund</span>
                  <span className="pay-card-value">
                    <span
                      className="pay-chip"
                      style={{ background: rChip.bg, color: rChip.color, borderColor: rChip.border }}
                    >
                      {rChip.label}
                    </span>
                  </span>
                </div>

                {userType === 'ADMIN' && checkUserPermissions('manage_payments') && row?.Order?.paymentStatus === 'PENDING' && (
                  <div className="pay-card-action-row">
                    <button
                      type="button"
                      className="pay-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateStatus(row?.Order?.id);
                      }}
                    >
                      <IoIosCheckmarkCircle size={16} />
                      <span>Mark as done</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="pay-empty">
            <div className="pay-empty-icon" aria-hidden="true">💳</div>
            <p className="pay-empty-title">No payments found</p>
            <p className="pay-empty-sub">Payments will appear here once orders are processed.</p>
          </div>
        )}
      </div>

      {/* Fixed bottom pagination bar (mobile only) */}
      {!loading && payment && payment.length > 0 && (() => {
        const totalPages = Math.max(1, Math.ceil((totalRows || 0) / (limit || 1)));
        const canPrev = page > 1;
        const canNext = page < totalPages;
        return (
          <div className="pay-mobile-pagination">
            <button
              type="button"
              className="pmp-btn"
              onClick={() => canPrev && handlePageChange(page - 1)}
              disabled={!canPrev}
              aria-label="Previous page"
            >
              ←
            </button>
            <span className="pmp-info">
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
              <span className="pmp-total">· {totalRows || 0} payments</span>
            </span>
            <button
              type="button"
              className="pmp-btn"
              onClick={() => canNext && handlePageChange(page + 1)}
              disabled={!canNext}
              aria-label="Next page"
            >
              →
            </button>
          </div>
        );
      })()}
    </div>
  )
}

export default Payment
